import { HTTPClient } from "@abaco/web-common/src/HTTP";
import { AxiosError } from "axios";
import { classToPlain, plainToClass } from "class-transformer";
import { API_PATH } from "../models/Constants";
import {
    DetailSections,
    LineItem,
    LineItems,
    Measures,
    ProjectProposal,
    ProjectProposalDetails,
    StandardOutput,
    StandardOutputs,
    LocationInvestmentParcels,
    PscClarification,
    EditablePscSection,
    DecisionLetterDTO,
    DecisionLetterAppeal
    
} from "../models/ProjectProposal";
import { Form, FormRow } from "../models/Form";

export default class ProjectProposalApi {
    private http: HTTPClient;
    private static instance: ProjectProposalApi | null = null;
    private basePath = `${API_PATH}/project`;
    private basePathPrint = `${API_PATH}`;

    private constructor(http: HTTPClient) {
        this.http = http;
    }

    public static getInstance(http: HTTPClient): ProjectProposalApi {
        if (this.instance) {
            return this.instance;
        }
        this.instance = new ProjectProposalApi(http);
        return this.instance;
    }

    async getMeasures(subjectId: string): Promise<[Measures | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
                .get<Measures>(`${this.basePath}/measure/${encodeURIComponent(subjectId)}`)
                .then(resp => {
                    resolve([plainToClass(Measures, resp.data), null]);
                })
                .catch(err => {
                    resolve([null, err]);
                });
        });
    }

    async createProjectProposal(
        subjectId: string,
        moduleCode: string
    ): Promise<[{ appId: number } | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
                .post<{ appId: number }>(
                    `${this.basePath}/create/${encodeURIComponent(subjectId)}/${encodeURIComponent(moduleCode)}`
                )
                .then(resp => {
                    resolve([resp.data, null]);
                })
                .catch(err => {
                    resolve([null, err as AxiosError]);
                });
        });
    }

    async getProjectProposal(
        subjectId: string,
        applicationId: string
    ): Promise<[ProjectProposal | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
                .get<ProjectProposal>(
                    `${this.basePath}/overview/${encodeURIComponent(subjectId)}/${encodeURIComponent(applicationId)}`
                )
                .then(resp => {
                    resolve([plainToClass(ProjectProposal, resp.data), null]);
                })
                .catch(err => {
                    resolve([null, err as AxiosError]);
                });
        });
    }

    async generatePrintData(subjectId: string, applicationId: string): Promise<[Blob | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
                .post(
                    `${this.basePathPrint}/print/data/generate/${encodeURIComponent(subjectId)}/${encodeURIComponent(applicationId)}`,
                    {}, // empty payload if none is required
                    { responseType: "blob" } // request binary data as blob
                )
                .then(resp => {
                    resolve([resp.data, null]);
                })
                .catch(err => {
                    resolve([null, err as AxiosError]);
                });
        });
    }
    
    async transitionProjectProposal(
        subjectId: string,
        applicationId: string,
        movementCode: string
    ): Promise<[boolean | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
                .post<boolean>(
                    `${this.basePath}/transition/${encodeURIComponent(subjectId)}/${encodeURIComponent(
                        applicationId
                    )}/${encodeURIComponent(movementCode)}`
                )
                .then(resp => {
                    resolve([resp.data, null]);
                })
                .catch(err => {
                    resolve([null, err as AxiosError]);
                });
        });
    }

    async getApplicationDocument(
        subjectId: string,
        applicationId: string,
        docCode: string
    ): Promise<[Form | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
                .get<Form>(
                    `${API_PATH}/form/${encodeURIComponent(subjectId)}/${encodeURIComponent(
                        applicationId
                    )}/doc/${encodeURIComponent(docCode)}`
                )
                .then(resp => {
                    resolve([plainToClass(Form, resp.data), null]);
                })
                .catch(err => {
                    resolve([null, err as AxiosError]);
                });
        });
    }

    async getPscClarificationDocument(
        subjectId: string,
        applicationId: string,
        psc_clarification: string
    ): Promise<[PscClarification | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
                .get<PscClarification>(
                    `${API_PATH}/form/${encodeURIComponent(subjectId)}/${encodeURIComponent(
                        applicationId
                    )}/${encodeURIComponent(psc_clarification)}`
                )
                .then(resp => {
                    resolve([resp.data, null]);
                })
                .catch(err => {
                    resolve([null, err as AxiosError]);
                });
        });
    }

    async submitPscClarificationAnswers(
        subjectId: string,
        applicationId: string,
        payload: { answers: { pkidQuestion: number; cpAnswer: string }[] }
      ): Promise<[boolean, AxiosError | null]> {
        return new Promise(resolve => {
          this.http
            .post(
              `${API_PATH}/form/${encodeURIComponent(subjectId)}/${encodeURIComponent(applicationId)}/psc_clarification`,
              payload
            )
            .then(() => resolve([true, null]))
            .catch(err => resolve([false, err as AxiosError]));
        });
    }
    
    async fetchEditableClarificationForms(
    subjectId: string,
    applicationId: string
    ): Promise<[EditablePscSection | null, AxiosError | null]> {
    return new Promise(resolve => {
        this.http
        .get<EditablePscSection>(
            `${API_PATH}/form/${encodeURIComponent(subjectId)}/${encodeURIComponent(applicationId)}/psc_clarification/editable`
        )
        .then(resp => {
            resolve([resp.data, null]);
        })
        .catch(err => {
            resolve([null, err as AxiosError]);
        });
    });
    }

    async getDecisionLetter(
        subjectId: string,
        applicationId: string
        ): Promise<[DecisionLetterDTO | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
            .get<DecisionLetterDTO>(
                `${API_PATH}/project/decision-letter/${encodeURIComponent(subjectId)}/${encodeURIComponent(applicationId)}`
            )
            .then(resp => {
                resolve([resp.data, null]);
            })
            .catch(err => {
                resolve([null, err as AxiosError]);
            });
        });
    }
    
    async appealDecisionLetter(
    subjectId: string,
    applicationId: string,
    payload: FormData
    ): Promise<[DecisionLetterAppeal | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
                .post<DecisionLetterAppeal>(
                    `${API_PATH}/project/decision-letter/${encodeURIComponent(subjectId)}/${encodeURIComponent(applicationId)}/appeal`,
                    payload
                )
                .then(resp => resolve([resp.data, null]))
                .catch(err => resolve([null, err as AxiosError]));
        });
    }

    async downloadDecisionLetter(
        fileId: number
        ): Promise<{ data: Blob; filename: string } | null> {
        try {
            const response = await this.http.get(
            `${API_PATH}/project/decision-letter/download/${encodeURIComponent(fileId)}`,
            { responseType: 'blob' }
            );
            let filename = 'downloaded-file';
            const contentDisposition = response.headers['content-disposition'];
            if (contentDisposition) {
            const match = contentDisposition.match(/filename="?([^"]+)"?/);
            if (match) filename = match[1];
            }
            return { data: response.data, filename };
        } catch (err) {
            console.error('Error downloading file:', err);
            return null;
        }
    }

    async getDocumentConfig(
        subjectId: string,
        applicationId: string,
        docCode: string
    ): Promise<[Form | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
                .get<Form>(
                    `${API_PATH}/form/${encodeURIComponent(subjectId)}/${encodeURIComponent(
                        applicationId
                    )}/doc-config/${encodeURIComponent(docCode)}`
                )
                .then(resp => {
                    resolve([plainToClass(Form, resp.data), null]);
                })
                .catch(err => {
                    resolve([null, err as AxiosError]);
                });
        });
    }

    async getDocumentUpload(
        subjectId: string,
        applicationId: string,
        docCode: string
    ): Promise<[Form | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
                .get<Form>(
                    `${API_PATH}/form/${encodeURIComponent(subjectId)}/${encodeURIComponent(
                        applicationId
                    )}/doc-upload/${encodeURIComponent(docCode)}`
                )
                .then(resp => {
                    resolve([plainToClass(Form, resp.data), null]);
                })
                .catch(err => {
                    resolve([null, err as AxiosError]);
                });
        });
    }

    async getDocumentConfigUpload(
        subjectId: string,
        applicationId: string,
        docCode: string
    ): Promise<[Form | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
                .get<Form>(
                    `${API_PATH}/form/${encodeURIComponent(subjectId)}/${encodeURIComponent(
                        applicationId
                    )}/doc-config-upload/${encodeURIComponent(docCode)}`
                )
                .then(resp => {
                    resolve([plainToClass(Form, resp.data), null]);
                })
                .catch(err => {
                    resolve([null, err as AxiosError]);
                });
        });
    }

    async createApplicationDocument(
        subjectId: string,
        applicationId: string,
        docCode: string,
        data: FormRow[]
    ): Promise<[Form | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
                .post<Form>(
                    `${API_PATH}/form/${encodeURIComponent(subjectId)}/${encodeURIComponent(
                        applicationId
                    )}/doc/${encodeURIComponent(docCode)}`,
                    classToPlain(data)
                )
                .then(resp => {
                    resolve([plainToClass(Form, resp.data), null]);
                })
                .catch(err => {
                    resolve([null, err as AxiosError]);
                });
        });
    }

    async setApplicationDocument(
        subjectId: string,
        applicationId: string,
        docCode: string,
        docId: number,
        data: FormRow[]
    ): Promise<[Form | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
                .patch<Form>(
                    `${API_PATH}/form/${encodeURIComponent(subjectId)}/${encodeURIComponent(
                        applicationId
                    )}/doc/${encodeURIComponent(docCode)}/${encodeURIComponent(docId)}`,
                    classToPlain(data)
                )
                .then(resp => {
                    resolve([plainToClass(Form, resp.data), null]);
                })
                .catch(err => {
                    resolve([null, err as AxiosError]);
                });
        });
    }

    async createDocumentUpload(
        subjectId: string,
        applicationId: string,
        docCode: string,
        data: object
    ): Promise<[Form | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
                .post<Form>(
                    `${API_PATH}/form/${encodeURIComponent(subjectId)}/${encodeURIComponent(
                        applicationId
                    )}/doc-upload/${encodeURIComponent(docCode)}`,
                    classToPlain(data)
                )
                .then(resp => {
                    resolve([plainToClass(Form, resp.data), null]);
                })
                .catch(err => {
                    resolve([null, err as AxiosError]);
                });
        });
    }

    async setDocumentUpload(
        subjectId: string,
        applicationId: string,
        docCode: string,
        docId: number,
        data: object
    ): Promise<[Form | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
                .patch<Form>(
                    `${API_PATH}/form/${encodeURIComponent(subjectId)}/${encodeURIComponent(
                        applicationId
                    )}/doc-upload/${encodeURIComponent(docCode)}/${encodeURIComponent(docId)}`,
                    classToPlain(data)
                )
                .then(resp => {
                    resolve([plainToClass(Form, resp.data), null]);
                })
                .catch(err => {
                    resolve([null, err as AxiosError]);
                });
        });
    }

    async getProjectProposalLineItems(
        subjectId: string,
        applicationId: string
    ): Promise<[LineItems | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
                .get<LineItems>(
                    `${this.basePath}/line-items/${encodeURIComponent(subjectId)}/${encodeURIComponent(applicationId)}`
                )
                .then(resp => {
                    resp.data.lineItems = resp.data.lineItems.map(e => {
                        const hash = LineItem.toHash(e);
                        return {
                            ...e,
                            hash
                        } as LineItem;
                    });
                    resolve([plainToClass(LineItems, resp.data), null]);
                })
                .catch(err => {
                    resolve([null, err as AxiosError]);
                });
        });
    }

    async setProjectProposalLineItems(
        subjectId: string,
        applicationId: string,
        data: LineItems
    ): Promise<[LineItems | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
                .post<LineItems>(
                    `${this.basePath}/line-items/${encodeURIComponent(subjectId)}/${encodeURIComponent(applicationId)}`,
                    classToPlain(data)
                )
                .then(resp => {
                    resp.data.lineItems = resp.data.lineItems.map(e => {
                        const hash = LineItem.toHash(e);
                        return {
                            ...e,
                            hash
                        } as LineItem;
                    });
                    resolve([plainToClass(LineItems, resp.data), null]);
                })
                .catch(err => {
                    resolve([null, err as AxiosError]);
                });
        });
    }

    async getDetailSections(
        subjectId: string,
        applicationId: string
    ): Promise<[DetailSections | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
                .get<Form>(
                    `${this.basePath}/detail-sections/${encodeURIComponent(subjectId)}/${encodeURIComponent(
                        applicationId
                    )}`
                )
                .then(resp => {
                    resolve([plainToClass(DetailSections, resp.data), null]);
                })
                .catch(err => {
                    resolve([null, err as AxiosError]);
                });
        });
    }

    async getProjectDetail(
        subjectId: string,
        applicationId: string
    ): Promise<[ProjectProposalDetails | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
                .get<Form>(
                    `${this.basePath}/details/${encodeURIComponent(subjectId)}/${encodeURIComponent(applicationId)}`
                )
                .then(resp => {
                    resolve([plainToClass(ProjectProposalDetails, resp.data), null]);
                })
                .catch(err => {
                    resolve([null, err as AxiosError]);
                });
        });
    }

    async setProjectDetails(
        subjectId: string,
        applicationId: string,
        data: ProjectProposalDetails
    ): Promise<[ProjectProposalDetails | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
                .post<ProjectProposalDetails>(
                    `${this.basePath}/details/${encodeURIComponent(subjectId)}/${encodeURIComponent(applicationId)}`,
                    classToPlain(data)
                )
                .then(resp => {
                    resolve([plainToClass(ProjectProposalDetails, resp.data), null]);
                })
                .catch(err => {
                    resolve([null, err as AxiosError]);
                });
        });
    }

    async getFiles(
        subjectId: string | number,
        fileId: string | number
    ): Promise<[{ data: ArrayBuffer; filename: string; contentType: string } | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
                .get<ArrayBuffer>(`${API_PATH}/download/${subjectId}/${fileId}`, {
                    responseType: "arraybuffer"
                })
                .then(resp => {
                    const contentType = resp.headers["content-type"];
                    const content = resp.headers["content-disposition"] as string;
                    let filename = "";
                    content
                        ?.split(";")
                        .map((e: string) => e.trim())
                        .forEach(val => {
                            const [key, value] = val.replace(/"/gi, "").split("=");
                            if (key === "filename") {
                                filename = value;
                            }
                        });

                    resolve([
                        {
                            data: resp.data,
                            contentType,
                            //url: `data:${contentType};base64,${base64String}`,
                            filename
                        },
                        null
                    ]);
                })
                .catch(err => {
                    resolve([null, err as AxiosError]);
                });
        });
    }

    async getProjectProposalStandardOutputs(
        subjectId: string,
        applicationId: string
    ): Promise<[StandardOutputs | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
                .get<StandardOutputs>(
                    `${this.basePath}/standard-output/${encodeURIComponent(subjectId)}/${encodeURIComponent(
                        applicationId
                    )}`
                )
                .then(resp => {
                    resp.data.standardOutputs = resp.data.standardOutputs.map(e => {
                        const hash = StandardOutput.toHash(e);
                        return {
                            ...e,
                            hash
                        } as StandardOutput;
                    });
                    resolve([plainToClass(StandardOutputs, resp.data), null]);
                })
                .catch(err => {
                    resolve([null, err as AxiosError]);
                });
        });
    }

    async setProjectProposalStandardOutputs(
        subjectId: string,
        applicationId: string,
        data: StandardOutputs
    ): Promise<[StandardOutputs | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
                .post<StandardOutputs>(
                    `${this.basePath}/standard-output/${encodeURIComponent(subjectId)}/${encodeURIComponent(
                        applicationId
                    )}`,
                    classToPlain(data)
                )
                .then(resp => {
                    resp.data.standardOutputs = resp.data.standardOutputs.map(e => {
                        const hash = StandardOutput.toHash(e);
                        return {
                            ...e,
                            hash
                        } as StandardOutput;
                    });
                    resolve([plainToClass(StandardOutputs, resp.data), null]);
                })
                .catch(err => {
                    resolve([null, err as AxiosError]);
                });
        });
    }

    async getLocationInvestmentParcels(
        subjectId: string,
        applicationId: string
    ): Promise<[LocationInvestmentParcels | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
                .get<LocationInvestmentParcels>(
                    `${this.basePath}/loc-investment/${encodeURIComponent(subjectId)}/${encodeURIComponent(
                        applicationId
                    )}`
                )
                .then(resp => {
                    resolve([plainToClass(LocationInvestmentParcels, resp.data), null]);
                })
                .catch(err => {
                    resolve([null, err as AxiosError]);
                });
        });
    }

    async setLocationInvestmentParcels(
        subjectId: string,
        applicationId: string,
        data: LocationInvestmentParcels
    ): Promise<[LocationInvestmentParcels | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
                .put<LocationInvestmentParcels>(
                    `${this.basePath}/loc-investment/${encodeURIComponent(subjectId)}/${encodeURIComponent(
                        applicationId
                    )}`,
                    classToPlain(data)
                )
                .then(resp => {
                    resolve([plainToClass(LocationInvestmentParcels, resp.data), null]);
                })
                .catch(err => {
                    resolve([null, err as AxiosError]);
                });
        });
    }
    //New change request
    async createChangeRequestNew(
        subjectId: string,
        parentAppId: string
    ): Promise<[{ application_id: number } | null, AxiosError | null]> {
        return new Promise(resolve => {
            this.http
                .post<{ application_id: number }>(
                    `${API_PATH}/project/change-request/create/${encodeURIComponent(subjectId)}/${encodeURIComponent(parentAppId)}`
                )
                .then(resp => {
                    resolve([resp.data, null]);
                })
                .catch(err => {
                    resolve([null, err as AxiosError]);
                });
        });
    }
}
